/**
 * Relógio da sessão (docs/spec/session-clock.md): cada sessão de jogo da mesa, do começo ao fim,
 * com as pausas no meio. Os mestres começam, pausam, voltam e encerram; todos veem o tempo correr.
 *
 * Como no áudio (audio.ts), o servidor guarda só o estado e o instante em que ele passou a valer
 * (`since`, no relógio do servidor), e cada cliente calcula o tempo de agora:
 *
 *     jogo agora = playedMs + (agora - since), enquanto corre
 *
 * O registro é a base das estatísticas, das gravações e da cobrança que ainda vêm: tudo o que muda
 * uma sessão passa por `applyGameSessionCommand`.
 */
import { z } from 'zod';
import { IdSchema, RoomRoleSchema, type GameSessionStep } from './domain.js';

const MsSchema = z.number().int().nonnegative();

export const GameSessionStatusSchema = z.enum(['running', 'paused', 'ended']);
export type GameSessionStatus = z.infer<typeof GameSessionStatusSchema>;

export const GameSessionSchema = z.object({
  id: IdSchema,
  /** o número na mesa: 1, 2, 3… */
  number: z.number().int().min(1),
  status: GameSessionStatusSchema,
  startedAt: MsSchema,
  /** correndo desde, pausada desde ou, encerrada, quando terminou (relógio do servidor) */
  since: MsSchema,
  /** tempo de jogo até `since`, sem as pausas */
  playedMs: MsSchema,
  /** as pausas já fechadas, somadas (a pausa em curso fica de fora) */
  pausedMs: MsSchema,
  /** quantas pausas já fecharam */
  pauses: z.number().int().nonnegative(),
});
export type GameSession = z.infer<typeof GameSessionSchema>;

export const GameSessionActionSchema = z.enum(['start', 'pause', 'resume', 'end']);
export type GameSessionAction = z.infer<typeof GameSessionActionSchema>;

/**
 * Comandos do relógio. `number` é a sessão em que o mestre clicou (no começar, a próxima): um
 * clique numa tela desatualizada não mexe em outra sessão.
 */
export type GameSessionCommand =
  | { type: 'start'; number: number; id: string }
  /** `at`: quando a pausa vale (a automática volta até a saída do último mestre); `auto`: foi o servidor */
  | { type: 'pause'; number: number; at?: number; auto?: boolean }
  | { type: 'resume'; number: number }
  | { type: 'end'; number: number };

/** `changed: false`: nada a fazer (clique repetido, sessão errada, nada aberto). */
export type GameSessionResult = { changed: false } | { changed: true; state: GameSession; step: GameSessionStep };

const UNCHANGED: GameSessionResult = { changed: false };

/** O número da próxima sessão da mesa. */
export function nextGameSessionNumber(latest: GameSession | null): number {
  return (latest?.number ?? 0) + 1;
}

/** Há uma sessão aberta (correndo ou em pausa)? */
export function isGameSessionOpen(session: GameSession | null | undefined): session is GameSession {
  return !!session && session.status !== 'ended';
}

/**
 * Os tempos da sessão em `now`: o jogo, as pausas fechadas e a pausa em curso. Nunca negativos,
 * mesmo com o relógio de quem pergunta atrasado.
 */
export function gameSessionTimes(session: GameSession, now: number): { playedMs: number; pausedMs: number; pauseMs: number } {
  const elapsed = Math.max(0, now - session.since);
  if (session.status === 'running') return { playedMs: session.playedMs + elapsed, pausedMs: session.pausedMs, pauseMs: 0 };
  if (session.status === 'paused') return { playedMs: session.playedMs, pausedMs: session.pausedMs, pauseMs: elapsed };
  return { playedMs: session.playedMs, pausedMs: session.pausedMs, pauseMs: 0 };
}

/**
 * Aplica um comando à última sessão da mesa (null: a mesa nunca teve uma). Puro: o servidor grava e
 * repassa o resultado.
 *
 * Encerrar durante uma pausa fecha a sessão na hora em que ela pausou, e essa última pausa não entra
 * nos totais: uma sessão esquecida em pausa (a automática, por exemplo) e encerrada dias depois
 * continua certa.
 */
export function applyGameSessionCommand(latest: GameSession | null, cmd: GameSessionCommand, now: number): GameSessionResult {
  if (cmd.type === 'start') {
    if (isGameSessionOpen(latest) || cmd.number !== nextGameSessionNumber(latest)) return UNCHANGED;
    const state: GameSession = { id: cmd.id, number: cmd.number, status: 'running', startedAt: now, since: now, playedMs: 0, pausedMs: 0, pauses: 0 };
    return { changed: true, state, step: { kind: 'start', number: cmd.number, at: now } };
  }
  if (!latest || latest.number !== cmd.number) return UNCHANGED;
  // o relógio do servidor pode voltar um pouco (ajuste do sistema): nada fica com tempo negativo
  const at = Math.max(now, latest.since);
  switch (cmd.type) {
    case 'pause': {
      if (latest.status !== 'running') return UNCHANGED;
      const effective = Math.min(Math.max(cmd.at ?? at, latest.since), at);
      const state: GameSession = { ...latest, status: 'paused', since: effective, playedMs: latest.playedMs + (effective - latest.since) };
      return { changed: true, state, step: { kind: 'pause', number: latest.number, at: effective, auto: !!cmd.auto } };
    }
    case 'resume': {
      if (latest.status !== 'paused') return UNCHANGED;
      const pauseMs = at - latest.since;
      const state: GameSession = { ...latest, status: 'running', since: at, pausedMs: latest.pausedMs + pauseMs, pauses: latest.pauses + 1 };
      return { changed: true, state, step: { kind: 'resume', number: latest.number, at, pauseMs } };
    }
    case 'end': {
      if (latest.status === 'ended') return UNCHANGED;
      const running = latest.status === 'running';
      const end = running ? at : latest.since;
      const state: GameSession = { ...latest, status: 'ended', since: end, playedMs: latest.playedMs + (running ? end - latest.since : 0) };
      return { changed: true, state, step: { kind: 'end', number: latest.number, at: end, playedMs: state.playedMs, pausedMs: state.pausedMs, pauses: state.pauses } };
    }
  }
}

/* ------------------------------------------------------------------ o registro (a aba Sessões) */

/** Os tetos do registro: o título e as notas do mestre, e quantas sessões a lista traz por vez. */
export const GAME_SESSION_LIMITS = { title: 80, notes: 5000, page: 50 } as const;

/*
 * As avaliações (docs/spec/session-clock.md, "Avaliações"): quem jogou dá de 1 a 5 estrelas à sessão,
 * e os mestres leem sem saber de quem é; cada mestre dá de 1 a 5 a cada jogador, e o jogador lê. Abrem
 * quando o mestre encerra a sessão e fecham 7 dias depois. O perfil mostra a média a partir de 5.
 */

/** O texto de uma avaliação, o prazo depois do fim e quantas avaliações o perfil pede para mostrar a média. */
export const GAME_SESSION_RATING = { comment: 1000, windowMs: 7 * 24 * 60 * 60 * 1000, profileMin: 5 } as const;

/**
 * Quando a avaliação fecha: 7 dias depois do pedido de fim. Não conta do fim efetivo, que numa sessão
 * esquecida em pausa fica dias antes, e a avaliação já nasceria fechada.
 */
export function ratingClosesAt(endRequestedAt: number): number {
  return endRequestedAt + GAME_SESSION_RATING.windowMs;
}

export const StarsSchema = z.number().int().min(1).max(5);

/** O que se manda ao avaliar: as estrelas e, se quiser, um texto. */
export const GameSessionRatingInputSchema = z.object({
  stars: StarsSchema,
  comment: z.string().trim().max(GAME_SESSION_RATING.comment).default(''),
});
export type GameSessionRatingInput = z.input<typeof GameSessionRatingInputSchema>;

/** Uma nota de jogador para a sessão, como os mestres a leem: sem quem deu nem quando. */
export const SessionRatingViewSchema = z.object({ stars: StarsSchema, comment: z.string() });
export type SessionRatingView = z.infer<typeof SessionRatingViewSchema>;

/** A nota que dei à sessão (eu joguei nela). */
export const MySessionRatingSchema = z.object({ stars: StarsSchema, comment: z.string(), updatedAt: MsSchema });
export type MySessionRating = z.infer<typeof MySessionRatingSchema>;

/** A nota de um mestre para um jogador, com o nome do mestre. */
export const PlayerRatingViewSchema = z.object({ gmId: IdSchema, gmName: z.string().max(255), stars: StarsSchema, comment: z.string(), updatedAt: MsSchema });
export type PlayerRatingView = z.infer<typeof PlayerRatingViewSchema>;

/** Um jogador da sessão, para os mestres: quem é e as notas que os mestres deram a ele. */
export const RatedPlayerSchema = z.object({ userId: IdSchema, userName: z.string().max(255), ratings: z.array(PlayerRatingViewSchema) });
export type RatedPlayer = z.infer<typeof RatedPlayerSchema>;

/**
 * As avaliações de uma sessão, com só o que quem pede pode ver: `mine` e `aboutMe` vêm para quem
 * jogou a sessão; `session` e `players`, para os mestres.
 */
export const GameSessionRatingsSchema = z.object({
  /** quando fecha; null com a sessão aberta (a avaliação abre no fim) */
  closesAt: MsSchema.nullable(),
  /** a nota que dei à sessão (null: ainda não dei); ausente: não joguei nela */
  mine: MySessionRatingSchema.nullable().optional(),
  /** as notas que os mestres me deram */
  aboutMe: z.array(PlayerRatingViewSchema).optional(),
  /** as notas dos jogadores, sem os nomes, e quantos jogadores a sessão teve */
  session: z.object({ ratings: z.array(SessionRatingViewSchema), players: z.number().int().nonnegative() }).optional(),
  /** cada jogador da sessão, com as notas dos mestres */
  players: z.array(RatedPlayerSchema).optional(),
});
export type GameSessionRatings = z.infer<typeof GameSessionRatingsSchema>;

/** As avaliações no cartão da sessão, na lista. */
export const GameSessionRatingBriefSchema = z.object({
  closesAt: MsSchema.nullable(),
  /** joguei a sessão: as estrelas que dei (null: ainda não dei) */
  mine: StarsSchema.nullable().optional(),
  /** mestre: a média das notas dos jogadores (null: nenhuma ainda) e quantas são */
  average: z.number().min(1).max(5).nullable().optional(),
  count: z.number().int().nonnegative().optional(),
  /** mestre: quantos jogadores da sessão eu ainda não avaliei */
  toRate: z.number().int().nonnegative().optional(),
});
export type GameSessionRatingBrief = z.infer<typeof GameSessionRatingBriefSchema>;

/**
 * Um aviso de avaliação (`POST /api/me/session-notices`): um mestre me avaliou (`gm`, com o nome
 * dele) ou jogadores avaliaram uma sessão da minha mesa (`players`, sem os nomes).
 */
export const GameSessionNoticeSchema = z.object({
  roomId: IdSchema,
  roomName: z.string(),
  number: z.number().int().min(1),
  kind: z.enum(['gm', 'players']),
  byName: z.string().optional(),
  /** quantas avaliações chegaram desde o último aviso */
  count: z.number().int().min(1),
});
export type GameSessionNotice = z.infer<typeof GameSessionNoticeSchema>;

/** Uma sessão na lista da aba Sessões: o estado, o título e quem começou e quem encerrou. */
export const GameSessionSummarySchema = GameSessionSchema.extend({
  title: z.string().max(GAME_SESSION_LIMITS.title),
  /** o fim efetivo (encerrada numa pausa: quando pausou); null com a sessão aberta */
  endedAt: MsSchema.nullable(),
  startedByName: z.string().max(255),
  endedByName: z.string().max(255).nullable(),
  /** as avaliações, como quem pede as vê (ausente: servidor sem avaliações, ou a sessão aberta) */
  ratings: GameSessionRatingBriefSchema.optional(),
});
export type GameSessionSummary = z.infer<typeof GameSessionSummarySchema>;

/** Um passo, como a linha do tempo o mostra: quando valeu, se foi o servidor e quem agiu. */
export const GameSessionEventViewSchema = z.object({
  kind: GameSessionActionSchema,
  at: MsSchema,
  automatic: z.boolean(),
  actorName: z.string().max(255),
});
export type GameSessionEventView = z.infer<typeof GameSessionEventViewSchema>;

/** Um trecho de presença: da entrada à saída da mesa (`leftAt` null: a pessoa ainda está lá). */
export const GameSessionPresenceViewSchema = z.object({
  userId: IdSchema.nullable(),
  userName: z.string().max(255),
  /** o papel na entrada */
  role: RoomRoleSchema,
  joinedAt: MsSchema,
  leftAt: MsSchema.nullable(),
});
export type GameSessionPresenceView = z.infer<typeof GameSessionPresenceViewSchema>;

/** O registro de uma sessão: o resumo com as notas, os passos, a presença e as avaliações. */
export const GameSessionDetailSchema = z.object({
  session: GameSessionSummarySchema.extend({ notes: z.string().max(GAME_SESSION_LIMITS.notes) }),
  events: z.array(GameSessionEventViewSchema),
  presence: z.array(GameSessionPresenceViewSchema),
  /** ausente: o servidor ainda não tem as avaliações */
  ratings: GameSessionRatingsSchema.optional(),
});
export type GameSessionDetail = z.infer<typeof GameSessionDetailSchema>;

/** O que o mestre muda numa sessão: o título e/ou as notas (texto simples, com as quebras de linha). */
export const GameSessionPatchSchema = z
  .object({
    title: z.string().trim().max(GAME_SESSION_LIMITS.title).optional(),
    notes: z.string().max(GAME_SESSION_LIMITS.notes).optional(),
  })
  .refine((patch) => patch.title !== undefined || patch.notes !== undefined);
export type GameSessionPatch = z.infer<typeof GameSessionPatchSchema>;
